import '@fontsource/public-sans';
import { CssBaseline, CssVarsProvider, extendTheme } from '@mui/joy';

import { ReactNode } from 'react';

const theme = extendTheme({
  fontFamily: {
    body: `"Inter", var(--joy-fontFamily-fallback, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol")`,
    display: `"Inter", "Inter", var(--joy-fontFamily-fallback, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol")`,
  },
  shadow: {
    xs: 'var(--joy-shadowRing, 0 0 #000), 0 1px 2px 0 rgba(var(--joy-shadowChannel, 187 187 187) / 0.12)',
    sm: 'var(--joy-shadowRing, 0 0 #000), 0px 0.8px 1.1px rgba(var(--joy-shadowChannel, 187 187 187) / 0.11), 0px 1.3px 1.8px -0.6px rgba(var(--joy-shadowChannel, 187 187 187) / 0.18), 0px 2.7px 3.8px -1.2px rgba(var(--joy-shadowChannel, 187 187 187) / 0.26)',
    md: 'var(--joy-shadowRing, 0 0 #000), 0px 0.8px 1.1px rgba(var(--joy-shadowChannel, 187 187 187) / 0.12), 0px 2.8px 3.9px -0.4px rgba(var(--joy-shadowChannel, 187 187 187) / 0.17), 0px 6.1px 8.6px -0.8px rgba(var(--joy-shadowChannel, 187 187 187) / 0.23),  0px 13.3px 18.8px -1.2px rgba(var(--joy-shadowChannel, 187 187 187) / 0.29)',
    lg: 'var(--joy-shadowRing, 0 0 #000), 0px 0.8px 1.1px rgba(var(--joy-shadowChannel, 187 187 187) / 0.11), 0px 4.5px 6.4px -0.2px rgba(var(--joy-shadowChannel, 187 187 187) / 0.13), 0px 7.9px 11.2px -0.4px rgba(var(--joy-shadowChannel, 187 187 187) / 0.16), 0px 12px 17px -0.5px rgba(var(--joy-shadowChannel, 187 187 187) / 0.19), 0px 17.5px 24.7px -0.7px rgba(var(--joy-shadowChannel, 187 187 187) / 0.21)',
    xl: 'var(--joy-shadowRing, 0 0 #000), 0px 0.8px 1.1px rgba(var(--joy-shadowChannel, 187 187 187) / 0.11), 0px 4.5px 6.4px -0.2px rgba(var(--joy-shadowChannel, 187 187 187) / 0.13), 0px 7.9px 11.2px -0.4px rgba(var(--joy-shadowChannel, 187 187 187) / 0.16), 0px 12px 17px -0.5px rgba(var(--joy-shadowChannel, 187 187 187) / 0.19), 0px 17.5px 24.7px -0.7px rgba(var(--joy-shadowChannel, 187 187 187) / 0.21), 0px 25.5px 36px -0.9px rgba(var(--joy-shadowChannel, 187 187 187) / 0.24), 0px 36.8px 52.1px -1.1px rgba(var(--joy-shadowChannel, 187 187 187) / 0.27), 0px 52.3px 74px -1.2px rgba(var(--joy-shadowChannel, 187 187 187) / 0.29)',
  },
  components: {
    JoyCheckbox: {
      styleOverrides: {
        checkbox: {
          borderColor: 'var(--border-primary)',
          '&:hover': {
            borderColor: 'var(--border-brand)',
            boxShadow:
              '0px 0px 0px 4px var(--bg-brand-secondary), 0px 1px 2px 0px #1018280D',
          },
          '&.Joy-checked': {
            backgroundColor: 'var(--bg-brand-primary)',
          },
        },
      },
    },
    JoyModal: {
      styleOverrides: {
        root: {
          'div[role="dialog"]': {
            backgroundColor: 'var(--bg-primary)',
          },
        },
        backdrop: {
          filter: 'none',
          backdropFilter: 'none',
          background: 'color-mix(in srgb, var(--bg-primary) 80%, transparent)',
        },
      },
    },
    JoyTooltip: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--text-primary)',
          color: 'var(--bg-secondary)',
          border: '1px solid var(--text-secondary)',
          '& .MuiTooltip-arrow': {
            color: 'var(--gray-25)',
          },
        },
      },
    },
  },
  colorSchemes: {
    dark: {
      palette: {
        primary: {
          50: '#d9eeec',
          100: '#b3dcd8',
          200: '#84c4c0',
          300: '#84c4c0',
          400: '#418b8a',
          500: '#2f6868',
          600: '#2b5a5a',
          700: '#264849',
          800: '#233e3e',
          900: '#102123',
        },
        neutral: {
          50: '#fafafa',
          100: '#f4f4f5',
          200: '#e4e4e7',
          300: '#d1d1d6',
          400: '#a0a0ab',
          500: '#70707b',
          600: '#51525c',
          700: '#3f3f46',
          800: '#26272b',
          900: '#1a1a1e',
        },
        background: {
          body: 'var(--bg-primary)',
        },
      },
    },
    light: {
      palette: {
        primary: {
          50: '#d9eeec',
          100: '#b3dcd8',
          200: '#84c4c0',
          300: '#84c4c0',
          400: '#418b8a',
          500: '#2f6868',
          600: '#2b5a5a',
          700: '#264849',
          800: '#233e3e',
          900: '#102123',
        },
        neutral: {
          50: '#fafafa',
          100: '#f4f4f5',
          200: '#e4e4e7',
          300: '#d1d1d6',
          400: '#a0a0ab',
          500: '#70707b',
          600: '#51525c',
          700: '#3f3f46',
          800: '#26272b',
          900: '#1a1a1e',
        },
      },
    },
  },
});

export function BaseApp(props: { children?: ReactNode }) {
  return (
    <CssVarsProvider
      theme={theme}
      defaultMode="system"
      modeStorageKey="langsmith-app-joy-mode"
    >
      <CssBaseline />
      {props.children}
    </CssVarsProvider>
  );
}
